<template>
  <div>
    <NuxtLayout name="base" :page="page">
      <LayoutContainer>
        <UtilityHeadline
          :class="{ 'dbc-text-color-primary': client !== 'cab' }"
          :text="$t('dbc-form-lost-and-found-h2')"
          :level="2"
        />

        <FormsSuccess v-if="successMessage" :successMessage="successMessage" />

        <DbcMessage
          v-if="!successMessage"
          severity="info"
          :message="$t('dbc-form-lost-and-found-message-info')"
        />

        <FormsBase
          v-if="!successMessage"
          :headline="$t('dbc-form-lost-and-found-h3')"
          :submitLabel="$t('dbc-form-lost-and-found-submit-label')"
          :privacyStatement="$t('dbc-form-lost-and-found-privacy-statement')"
          :mandatoryFields="$t('dbc-form-mandatory-fields')"
          :isLoading="isLoading"
          :errorMessage="serverError"
          name="rad-melden"
          @submit="(event) => submit(event as FormDataLostAndFound)"
        >
          <template #left>
            <FormKit
              type="tel"
              name="bikeNumber"
              :label="$t('dbc-form-lost-and-found-input-bike-number-label')"
              :placeholder="
                $t('dbc-form-lost-and-found-input-bike-number-placeholder')
              "
              maxlength="5"
              validation="required|number|length:5,5"
              validation-visibility="blur"
              :validation-messages="{
                required: $t('dbc-form-lost-and-found-input-bike-number-error'),
                number: $t('dbc-form-lost-and-found-input-bike-number-error'),
                length: $t('dbc-form-lost-and-found-input-bike-number-error'),
              }"
            />

            <div>
              <UtilityText
                :text="`${$t(
                  'dbc-form-lost-and-found-notification-about-location-question'
                )}*`"
                class="dbc-cancel-contract-mandatory-headline"
              />

              <Tabs :value="selectedTab" @update:value="onUpdateTabs">
                <TabList v-bind="tabListPassThrough">
                  <Tab
                    value="0"
                    as="div"
                    class="cursor-pointer border-solid border-0 border-b-[3px] border-transparent aria-selected:border-red-500"
                  >
                    {{
                      $t(
                        'dbc-form-lost-and-found-notification-about-location-gps'
                      )
                    }}</Tab
                  >
                  <Tab
                    value="1"
                    as="div"
                    class="cursor-pointer border-solid border-0 border-b-[3px] border-transparent aria-selected:border-red-500"
                    >{{
                      $t(
                        'dbc-form-lost-and-found-notification-about-location-address'
                      )
                    }}</Tab
                  >
                </TabList>
                <TabPanels>
                  <TabPanel value="0">
                    <FormKit
                      type="checkbox"
                      :label="$t('dbc-form-lost-and-found-checkbox-gps-label')"
                      :help="$t('dbc-form-lost-and-found-checkbox-gps-help')"
                      name="gps"
                      ref="gps"
                      id="gps"
                      :value="false"
                      :validation="selectedTab === '0' ? 'accepted' : ''"
                      validation-visibility="blur"
                      :validation-messages="{
                        accepted: $t(
                          'dbc-form-lost-and-found-checkbox-gps-error'
                        ),
                      }"
                      @click="onClickGPS"
                    />
                  </TabPanel>
                  <TabPanel value="1">
                    <FormKit
                      type="dbc_autocomplete"
                      name="autocomplete"
                      :placeholder="
                        $t('dbc-form-lost-and-found-input-address-placeholder')
                      "
                      :validation="selectedTab === '1' ? 'required' : ''"
                      :validation-messages="{
                        required: $t(
                          'dbc-form-lost-and-found-input-address-error'
                        ),
                      }"
                      validation-visibility="blur"
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </div>
          </template>
          <template #right>
            <FormKit
              type="textarea"
              name="comment"
              :label="
                $t(
                  'dbc-form-lost-and-found-textarea-location-description-label'
                )
              "
              rows="10"
              :placeholder="
                $t(
                  'dbc-form-lost-and-found-textarea-location-description-placeholder'
                )
              "
              maxlength="250"
              validation="required|length:20,250"
              validation-visibility="blur"
              :validation-messages="{
                length: $t(
                  'dbc-form-lost-and-found-textarea-location-description-error'
                ),
              }"
            />
          </template>
        </FormsBase>
      </LayoutContainer>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { type FormKitNode, getNode, createMessage } from '@formkit/core'
import type { UseGeolocationReturn } from '@vueuse/core'
import { useGeolocation } from '@vueuse/core'
import { ref, onMounted } from 'vue'
import type { Ref } from 'vue'
import { useRoute } from 'vue-router'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import TabPanel from 'primevue/tabpanel'

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const client = runtimeConfig.public.client

const { t, setLocale } = useI18n()
const locale = route.params.locale as string
setLocale(locale)

const isLoading = ref(false)
const serverError = ref<string | null>(null)
const gpsChecked = ref(false)
const geolocation = ref<UseGeolocationReturn>()
const successMessage = ref<string>('')
const selectedTab = ref('0')

function onUpdateTabs(tab: string) {
  selectedTab.value = tab
}

geolocation.value = useGeolocation({ immediate: false })

function onClickGPS() {
  gpsChecked.value = !gpsChecked.value
  if (gpsChecked.value === true) {
    geolocation.value!.resume()
  } else {
    geolocation.value!.pause()
  }
}

onMounted(() => {
  navigator.permissions
    .query({ name: 'geolocation' })
    .then((permissionStatus) => {
      permissionStatus.onchange = () => {
        if (permissionStatus.state === 'granted') {
          geolocation.value = useGeolocation()
          const gpsNode = getNode('gps') as FormKitNode
          gpsNode.store.filter((message) => message.key !== 'geo')
        }
      }
    })
})

async function submit(data: FormDataLostAndFound): Promise<void> {
  isLoading.value = true

  const { bikeNumber, comment } = data

  const location: GeoJsonPoint = {}

  if (data.gps) {
    const gpsNode = getNode('gps') as FormKitNode
    const geoCoords = ref(geolocation.value?.coords)
    const latitude = geoCoords?.value?.latitude
    const longitude = geoCoords?.value?.longitude
    const geoError = ref(geolocation.value?.error)

    if (
      latitude === Infinity ||
      longitude === Infinity ||
      geoError.value?.code
    ) {
      const message = createMessage({
        key: 'geo',
        blocking: false,
        value: t('dbc-form-lost-and-found-checkbox-gps-error'),
      })

      gpsNode.store.set(message)
      isLoading.value = false

      return
    }

    location.latitude = latitude
    location.longitude = longitude
  }

  const body: PostBodyBikesFound = {
    bikeNumber,
    comment,
    location,
    language: locale,
  }

  if (data.autocomplete) {
    body.address = data.autocomplete.address
    body.location = data.autocomplete.location
  }

  const response = await fetchBikeFound(body)

  if (response.status === 201) {
    successMessage.value = t('dbc-form-lost-and-found-message-success')
  }

  if (!response.ok) {
    if (response.data?.message) {
      // bike not found error
      serverError.value = response.data.message
    } else {
      serverError.value = t('dbc-form-lost-and-found-error-server')
    }
  }

  isLoading.value = false
}

const page: Page = {
  id: 0,
  title: 'Lost and found',
  path: locale === 'en' ? '/report-a-bike' : '/rad-melden',
  description: '',
  content: [],
  localizations: [
    {
      id: 0,
      title: 'Lost and found',
      path: 'rad-melden',
      locale: 'de',
    },
    {
      id: 1,
      title: 'Lost and found',
      path: 'report-a-bike',
      locale: 'en',
    },
  ],
  locale: locale as string,
}

usePageHead(page)

const tabListPassThrough = {
  pt: {
    tablist: {
      class: 'flex gap-l mb-xs',
    },
    inkbar: {
      class: 'hidden',
    },
  },
}
</script>
